/**
 * Slider timing derivation: turns a slider's geometric `length` and
 * `slides` count into the discrete beat-events the engine + renderer
 * need (`endBeat`, `tickBeats`, `repeatBeats`).
 *
 * osu's slider velocity formula (from the wiki):
 *   span_duration_ms = length_units / (sliderMultiplier × 100 × velocityMultiplier)
 *                       × beatLength_ms
 *
 * Where:
 *   - `length_units` — the slider's geometric path length in osu units
 *     (the 8th field on a slider's `[HitObjects]` row).
 *   - `sliderMultiplier` — chart-level base velocity from
 *     `[Difficulty] SliderMultiplier`. Default 1.4.
 *   - `velocityMultiplier` — the active inherited-timing-point's
 *     velocity factor. An inherited timing point with `beatLength = -X`
 *     contributes `multiplier = 100 / X`; absent or non-inherited
 *     timing point at this slider's start contributes 1.0.
 *   - `beatLength_ms` — wall-clock ms per beat from the active
 *     uninherited timing point.
 *
 * Re-expressing in beats (`span_beats = span_duration_ms / beatLength_ms`)
 * makes the formula independent of bpm:
 *   span_beats = length_units / (sliderMultiplier × 100 × velocityMultiplier)
 *
 * Tick placement (osu wiki "Slider ticks"):
 *   - Tick interval = `1 / sliderTickRate` beats per tick.
 *   - Ticks fire at every multiple of `tickInterval` from each span's
 *     head, while strictly less than the span's tail (i.e. exclusive at
 *     the tail).
 *   - Ticks within `tickInterval / 2` of the span's tail are dropped to
 *     avoid a near-tail tick stealing focus from the tail/repeat edge
 *     itself.
 *
 * Repeat edges:
 *   - A slider with `slides = N` has `N - 1` repeat edges between
 *     spans, at `head + span_beats × i` for `i = 1, …, N - 1`.
 *
 * The engine uses these arrays to drive scoring (a tick or repeat
 * landing while the cursor is held inside the follow-circle awards a
 * combo+score event); the renderer draws reverse arrows at each
 * repeat edge until consumed.
 */

export interface DeriveSliderTimingArgs {
  /** Slider head beat. */
  headBeat: number;
  /** Geometric length in osu units. */
  length: number;
  /** Slides count from the source `.osu` row. 1 = no repeats. */
  slides: number;
  /** Chart-level `[Difficulty] SliderMultiplier`. Default 1.4. */
  sliderMultiplier: number;
  /** Chart-level `[Difficulty] SliderTickRate`. Default 1.0. */
  sliderTickRate: number;
  /**
   * Active inherited-timing-point velocity factor at the slider's
   * start (1.0 when no inherited point is active, or `100 / |beatLength|`
   * for an inherited point with negative `beatLength`).
   */
  velocityMultiplier: number;
}

export interface SliderTiming {
  /** Beats per traversal (head→tail). */
  spanBeats: number;
  /** Total slider length in beats (`spanBeats × slides`). */
  totalBeats: number;
  /** Beat the slider's last edge resolves at. */
  endBeat: number;
  /**
   * Absolute beats where slider ticks fire. Excludes the head, tail,
   * and repeat edges. Sorted ascending.
   */
  tickBeats: number[];
  /**
   * Absolute beats where the slider reverses direction (one entry
   * per intermediate edge between spans). Empty when `slides === 1`.
   * Sorted ascending.
   */
  repeatBeats: number[];
}

/**
 * Compute the slider ball's distance along the curve at the given
 * beat. Sliders alternate direction every span — slide 0 traces
 * head→tail at progress p, slide 1 traces tail→head at progress
 * (1 - p), etc. The returned distance feeds straight into
 * {@link import('./osuCurves.js').pointAtDistance} on the slider's
 * geometric path.
 *
 * Both engine (tracking / scoring) and renderer (drawing the ball)
 * call this — keeping it in one place ensures the ball position
 * the player sees matches the position the engine evaluates
 * tracking against.
 *
 * Caller pre-computed inputs (vs. recomputing them per query):
 * `endBeat`, `slides`, and `length` come straight from the slider's
 * persisted hit-object data, so the cost is just a couple of
 * arithmetic ops + a parity check per call.
 */
export function sliderArcLengthAt(args: {
  headBeat: number;
  endBeat: number;
  length: number;
  slides: number;
  beat: number;
}): number {
  const { headBeat, endBeat, length, slides, beat } = args;
  const totalBeats = endBeat - headBeat;
  if (totalBeats <= 0 || slides <= 0) {
    return 0;
  }
  const spanBeats = totalBeats / slides;
  if (spanBeats <= 0) {
    return 0;
  }
  const elapsed = beat - headBeat;
  // Clamp to [0, totalBeats] so a query past endBeat returns the
  // ball's final position (whichever endpoint of the curve the
  // slides parity lands on) rather than running off the end.
  const clamped = Math.max(0, Math.min(totalBeats, elapsed));
  const span = Math.min(slides - 1, Math.floor(clamped / spanBeats));
  const progress = clamped / spanBeats - span;
  return span % 2 === 0 ? progress * length : (1 - progress) * length;
}

/**
 * Hard cap on {@link sliderTickDistances} output so a pathologically
 * long slider (or a tiny spacing) can't grow the renderer's per-frame
 * draw list without bound. Real sliders sit well under this — a 2000-
 * unit spiral at the densest sane spacing is ~30 markers.
 */
export const MAX_SLIDER_TICK_MARKERS = 64;

/**
 * Evenly-spaced distances (osu units, from the head along the single
 * head→tail span) at which to draw a slider's tick markers — a built-in
 * ruler for the slider's on-screen length. Markers sit at `spacing`,
 * `2 × spacing`, … up to (but not including) the tail, so a slider only
 * slightly longer than `spacing` shows exactly one marker at `spacing`,
 * one twice as long shows two, and a slider shorter than `spacing` shows
 * none.
 *
 * Spacing is a fixed *distance*, deliberately decoupled from the chart's
 * beat-based `sliderTickRate` / scored `tickBeats`: those vanish on a
 * slider whose *beat* span is shorter than one tick interval (common on
 * high-SV maps), even when it covers a long stretch of screen. A
 * distance ruler instead tracks what the player actually sees, so a
 * long-but-fast slider still gets markers. Repeat passes retrace the
 * same span, so the renderer draws each marker once and the ball sweeps
 * over it again on every slide.
 */
export function sliderTickDistances(length: number, spacing: number): number[] {
  // `!(x > 0)` rather than `x <= 0` so NaN / non-finite inputs bail too.
  if (!(length > 0) || !(spacing > 0)) {
    return [];
  }
  const distances: number[] = [];
  for (
    let k = 1;
    k * spacing < length && distances.length < MAX_SLIDER_TICK_MARKERS;
    k++
  ) {
    distances.push(k * spacing);
  }
  return distances;
}

export function deriveSliderTiming(args: DeriveSliderTimingArgs): SliderTiming {
  const {
    headBeat,
    length,
    slides,
    sliderMultiplier,
    sliderTickRate,
    velocityMultiplier,
  } = args;
  if (length <= 0 || sliderMultiplier <= 0 || velocityMultiplier <= 0 || slides < 1) {
    return {
      spanBeats: 0,
      totalBeats: 0,
      endBeat: headBeat,
      tickBeats: [],
      repeatBeats: [],
    };
  }
  const spanBeats = length / (sliderMultiplier * 100 * velocityMultiplier);
  const totalBeats = spanBeats * slides;
  const endBeat = headBeat + totalBeats;
  const tickInterval =
    Number.isFinite(sliderTickRate) && sliderTickRate > 0 ? 1 / sliderTickRate : 0;
  const tickBeats: number[] = [];
  if (tickInterval > 0 && tickInterval < spanBeats) {
    for (let s = 0; s < slides; s++) {
      const spanStart = headBeat + spanBeats * s;
      const spanEnd = spanStart + spanBeats;
      // Skip ticks that land within tickInterval / 2 of the span's
      // tail. The osu! wiki says tickDistance/2 in path units; in
      // beat space that's tickInterval/2 because beat-space and
      // distance-space are linearly related within a span.
      const lastValidTickBeat = spanEnd - tickInterval / 2;
      for (let i = 1; ; i++) {
        const tb = spanStart + tickInterval * i;
        if (tb >= lastValidTickBeat) {
          break;
        }
        tickBeats.push(tb);
      }
    }
  }
  const repeatBeats: number[] = [];
  for (let i = 1; i < slides; i++) {
    repeatBeats.push(headBeat + spanBeats * i);
  }
  return {
    spanBeats,
    totalBeats,
    endBeat,
    tickBeats,
    repeatBeats,
  };
}

/**
 * Walk an osu `[TimingPoints]` list and produce a function that
 * returns the active velocity multiplier (and beat-length-derived
 * info) at any source-time `timeMs`. Used by the importer when
 * deriving per-slider timing.
 *
 * Inherited timing points (`uninherited === false`) carry a negative
 * `beatLength` whose magnitude encodes a percentage:
 *   `multiplier = 100 / |beatLength|`
 * so a `-50` beatLength = 2× velocity, `-200` = 0.5× velocity.
 *
 * Uninherited timing points carry the bpm/beat-length for the
 * current section; an uninherited point also resets the velocity
 * multiplier to 1.0.
 *
 * Output is a closure rather than a sorted array so callers can
 * query ad-hoc without re-sorting.
 */
export interface OsuTimingPointForSlider {
  timeMs: number;
  beatLength: number;
  uninherited: boolean;
}

export interface SliderTimingLookup {
  /** Velocity multiplier active at the given source `timeMs`. */
  velocityMultiplierAt(timeMs: number): number;
}

export function buildSliderTimingLookup(
  points: ReadonlyArray<OsuTimingPointForSlider>,
): SliderTimingLookup {
  const sorted = [...points].sort((a, b) => a.timeMs - b.timeMs);
  return {
    velocityMultiplierAt(timeMs: number): number {
      // Walk back-to-front and pick up the most recent inherited
      // point's multiplier, resetting on any uninherited point we
      // cross. We never see SV changes after our query time (osu's
      // runtime semantics: "active at T" means the most recent
      // timing point ≤ T).
      let multiplier = 1.0;
      for (const tp of sorted) {
        if (tp.timeMs > timeMs) {
          break;
        }
        if (tp.uninherited) {
          multiplier = 1.0;
        } else if (tp.beatLength < 0) {
          // Inherited points encode `multiplier = -100 / beatLength`.
          // Clamp to a sane range — observed in the wild: 0.1–10×.
          // Outside this span the chart is almost certainly corrupt.
          const m = -100 / tp.beatLength;
          if (Number.isFinite(m) && m > 0) {
            multiplier = Math.max(0.1, Math.min(10, m));
          }
        }
      }
      return multiplier;
    },
  };
}
